/**
 * Library Loader - Idempotent loading of external libraries from CDN
 * Supports both UMD (via DOM injection) and ESM (via dynamic import)
 * 
 * @module libraryLoader
 * @version 1.0.1
 * @since 3.2.31
 * 
 * Global state (for debugging and idempotency):
 * - window.BF_librariesESM - Map of loaded ESM modules (url -> module)
 * - window.BF_librariesPending - Map of in-flight loads (url -> Promise)
 */

// Initialize global Maps for tracking
if (!window.BF_librariesESM) {
  window.BF_librariesESM = new Map();
}
if (!window.BF_librariesPending) {
  window.BF_librariesPending = new Map();
}

/**
 * Load an external library once (idempotent)
 * 
 * @param {string} url - The CDN URL to load
 * @param {Object} options - Loading options
 * @param {string} options.type - 'script' (default) or 'stylesheet'
 * @param {string} options.moduleType - 'classic' (default) or 'module' for ESM
 * @param {number} options.timeout - Timeout in milliseconds (default 30000)
 * @param {Object} options.attributes - Additional attributes to set on element (e.g., integrity, crossorigin)
 * 
 * @returns {Promise<Object|Module>} 
 *   - For UMD: {url, type, element, cached}
 *   - For ESM: Module object
 * 
 * @throws {Error} If URL is missing, type is invalid, or loading fails
 * 
 * @example
 * // Load UMD script
 * await loadLibraryOnce('https://cdn.jsdelivr.net/npm/chart.js@4.0.0/dist/chart.umd.js');
 * 
 * @example
 * // Load ESM module
 * const Chart = await loadLibraryOnce('https://cdn.jsdelivr.net/npm/chart.js@4.0.0/+esm', {
 *   moduleType: 'module'
 * });
 * 
 * @example
 * // Load stylesheet with SRI
 * await loadLibraryOnce('https://cdn.example.com/styles.css', {
 *   type: 'stylesheet',
 *   attributes: {
 *     integrity: 'sha256-...',
 *     crossorigin: 'anonymous'
 *   }
 * });
 */
export async function loadLibraryOnce(url, options = {}) {
  // Validate URL
  if (!url || typeof url !== 'string' || url.trim() === '') {
    throw new Error('URL is required and must be a non-empty string');
  }

  // Parse options with defaults
  const {
    type = 'script',
    moduleType = 'classic',
    timeout = 30000,
    attributes = {}
  } = options;

  // Validate type
  const validTypes = ['script', 'stylesheet'];
  if (!validTypes.includes(type)) {
    throw new Error(`Invalid type "${type}". Must be one of: ${validTypes.join(', ')}`);
  }

  // ESM module loading
  if (moduleType === 'module') {
    return await loadESMModule(url, timeout);
  }

  // UMD/Classic loading via DOM injection
  if (type === 'script') {
    return await loadScript(url, timeout, attributes);
  } else if (type === 'stylesheet') {
    return await loadStylesheet(url, timeout, attributes);
  }
}

/**
 * Load an ESM module using <script type="module"> wrapper
 * This bypasses webpack's import() transformation
 * Uses window.BF_librariesESM Map for idempotency
 * 
 * @private
 * @param {string} url - The module URL
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Module>} The imported module
 */
async function loadESMModule(url, timeout) {
  // Check if already loaded in memory cache
  if (window.BF_librariesESM.has(url)) {
    return window.BF_librariesESM.get(url);
  }
  
  // Check if already exists in DOM (page refresh case)
  const existing = document.querySelector(`script[type="module"][data-esm-url="${url}"]`);
  if (existing) {
    // Script exists but not in cache - must reload
    // This is rare (cache cleared but DOM persisted)
    // Fall through to load
  }
  
  return new Promise((resolve, reject) => {
    // Generate unique event name to avoid collisions
    const eventName = 'bf-esm-loaded-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    
    // Sanitize URL to prevent XSS in template literal
    const safeUrl = url.replace(/'/g, "\\'").replace(/\r?\n/g, '');
    
    // Create script tag with type="module"
    const script = document.createElement('script');
    script.type = 'module';
    script.setAttribute('data-esm-url', url); // Track for idempotency
    
    // The import() inside this string is NOT transformed by webpack
    // because it's runtime string content, not build-time code
    script.textContent = `
      try {
        const module = await import('${safeUrl}');
        window.dispatchEvent(new CustomEvent('${eventName}', {
          detail: { module, success: true, url: '${safeUrl}' }
        }));
      } catch (error) {
        window.dispatchEvent(new CustomEvent('${eventName}', {
          detail: { error: error.message, success: false, url: '${safeUrl}' }
        }));
      }
    `;
    
    // Timeout handler
    const timeoutId = setTimeout(() => {
      cleanup(false);
      reject(new Error(`Timeout loading ESM module from ${url} after ${timeout}ms`));
    }, timeout);
    
    // Success/error handler
    const listener = (e) => {
      const success = e.detail.success;
      cleanup(success);
      
      if (success) {
        // Cache the module for idempotency
        window.BF_librariesESM.set(url, e.detail.module);
        resolve(e.detail.module);
      } else {
        reject(new Error(`Failed to load ESM module from ${url}: ${e.detail.error}`));
      }
    };
    
    // Cleanup function
    const cleanup = (keepScript) => {
      clearTimeout(timeoutId);
      window.removeEventListener(eventName, listener);
      // Keep script tag in DOM on success for visibility
      // Remove on error/timeout to avoid clutter
      if (!keepScript && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
    
    // Attach event listener
    window.addEventListener(eventName, listener);
    
    // Script tag error handler (syntax errors, etc)
    script.onerror = () => {
      cleanup(false);
      reject(new Error(`Failed to create module script for ${url}`));
    };
    
    // Append to DOM (triggers execution)
    document.head.appendChild(script);
  });
}

/**
 * Internal helper: apply arbitrary attributes to a DOM element
 * @private
 */
function applyAttributes(element, attributes) {
  Object.keys(attributes).forEach(key => {
    element.setAttribute(key, attributes[key]);
  });
}

/**
 * Internal helper: generic loader for DOM-based resources (script/stylesheet)
 * Handles idempotency, timeout, event wiring, cleanup, and race conditions
 * @private
 * @param {Object} params
 * @param {string} params.url
 * @param {number} params.timeout
 * @param {Object} params.attributes
 * @param {string} params.type - 'script' | 'stylesheet'
 * @param {() => HTMLElement} params.createElement - factory for the element with core props set
 * @param {string} params.existingSelector - selector to detect existing element
 */
async function loadDomElement({ url, timeout, attributes, type, createElement, existingSelector }) {
  // Check if load is already in progress (race condition protection)
  const pendingKey = `${type}:${url}`;
  if (window.BF_librariesPending.has(pendingKey)) {
    return await window.BF_librariesPending.get(pendingKey);
  }
  
  // Check if already loaded in DOM
  const existing = document.querySelector(existingSelector);
  if (existing) {
    return {
      url,
      type,
      element: existing,
      cached: true
    };
  }

  // Start new load and track it
  const loadPromise = new Promise((resolve, reject) => {
    const element = createElement();
    applyAttributes(element, attributes);

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Timeout loading ${type} from ${url} after ${timeout}ms`));
    }, timeout);

    const onLoad = () => {
      cleanup();
      resolve({ url, type, element, cached: false });
    };

    const onError = () => {
      cleanup();
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
      reject(new Error(`Failed to load ${type} from ${url}`));
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      element.removeEventListener('load', onLoad);
      element.removeEventListener('error', onError);
      // Remove from pending map when done
      window.BF_librariesPending.delete(pendingKey);
    };

    element.addEventListener('load', onLoad);
    element.addEventListener('error', onError);

    document.head.appendChild(element);
  });
  
  // Track in-flight load
  window.BF_librariesPending.set(pendingKey, loadPromise);
  
  return loadPromise;
}

/**
 * Load a script via DOM injection
 * Checks for existing script first (idempotent)
 * 
 * @private
 * @param {string} url - The script URL
 * @param {number} timeout - Timeout in milliseconds
 * @param {Object} attributes - Additional attributes
 * @returns {Promise<Object>} Result object {url, type, element, cached}
 */
async function loadScript(url, timeout, attributes) {
  return loadDomElement({
    url,
    timeout,
    attributes,
    type: 'script',
    existingSelector: `script[src="${url}"]`,
    createElement: () => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      return script;
    }
  });
}

/**
 * Load a stylesheet via DOM injection
 * Checks for existing stylesheet first (idempotent)
 * 
 * @private
 * @param {string} url - The stylesheet URL
 * @param {number} timeout - Timeout in milliseconds
 * @param {Object} attributes - Additional attributes
 * @returns {Promise<Object>} Result object {url, type, element, cached}
 */
async function loadStylesheet(url, timeout, attributes) {
  return loadDomElement({
    url,
    timeout,
    attributes,
    type: 'stylesheet',
    existingSelector: `link[rel="stylesheet"][href="${url}"]`,
    createElement: () => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      return link;
    }
  });
}

/**
 * Helper: Check if a library is already loaded
 * Checks both DOM (UMD/stylesheets) and memory cache (ESM)
 * 
 * @param {string} url - The library URL
 * @returns {boolean} True if library is loaded
 * 
 * @example
 * if (isLibraryLoaded('https://cdn.example.com/lib.js')) {
 *   console.log('Already loaded');
 * }
 */
export function isLibraryLoaded(url) {
  // Check UMD scripts in DOM
  const scriptExists = !!document.querySelector(`script[src="${url}"]`);
  
  // Check stylesheets in DOM
  const stylesheetExists = !!document.querySelector(`link[rel="stylesheet"][href="${url}"]`);
  
  // Check ESM modules in cache
  const esmExists = window.BF_librariesESM && window.BF_librariesESM.has(url);
  
  // Check ESM scripts in DOM (kept for visibility)
  const esmScriptExists = !!document.querySelector(`script[type="module"][data-esm-url="${url}"]`);
  
  return scriptExists || stylesheetExists || esmExists || esmScriptExists;
}

/**
 * Helper: Get all loaded library URLs
 * Includes UMD scripts, stylesheets, and ESM modules
 * 
 * @returns {Array<string>} Array of loaded library URLs
 * 
 * @example
 * const loaded = getLoadedLibraries();
 * console.log('Loaded libraries:', loaded);
 */
export function getLoadedLibraries() {
  // UMD scripts in DOM
  const scripts = Array.from(document.querySelectorAll('script[src]')).map(el => el.src);
  
  // Stylesheets in DOM
  const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).map(el => el.href);
  
  // ESM modules from cache
  const esmModules = window.BF_librariesESM ? Array.from(window.BF_librariesESM.keys()) : [];
  
  // Combine and remove duplicates
  return [...new Set([...scripts, ...stylesheets, ...esmModules])];
}

// Export as default for convenient import
export default loadLibraryOnce;

